// Company Profile Types for AI Training

export interface CompanyInfo {
  companyName?: string;
  foundedDate?: string;
  companyType?: 'sahis' | 'limited' | 'anonim' | 'kooperatif' | 'diger';
  employeeCount?: number;
  sector?: string;
  targetAudience?: 'b2b' | 'b2c' | 'b2b2c';
  country?: string;
  city?: string;
  address?: string;
  warehouseLocations?: { city: string; count: number }[];
}

export interface FinancialInfo {
  monthlyRevenue?: number;
  revenueCurrency?: 'TRY' | 'USD' | 'EUR';
  targetProfitMargin?: number;
  adBudget?: {
    website?: number;
    marketplace?: number;
    socialMedia?: number;
    influencer?: number;
    other?: number;
  };
}

export interface ProductStockInfo {
  productionType?: 'manufacturer' | 'reseller' | 'both';
  averageLeadTime?: number; // days
  minStockPolicy?: number;
}

export interface BusinessGoals {
  yearlyGrowthTarget?: number;
  expansionPlans?: string[];
  competitors?: { name: string; website?: string }[];
  strengths?: string[];
  weaknesses?: string[];
}

export interface CustomerServiceHours {
  enabled: boolean;
  type?: 'sales' | 'support' | 'both';
  channels?: ('phone' | 'chat' | 'email' | 'whatsapp')[];
  workingHours?: string;
}

export interface OperationalInfo {
  cargoCompanies?: string[];
  customerServiceHours?: CustomerServiceHours;
  seasonalPeriods?: string[];
}

export interface EmailMarketing {
  enabled: boolean;
  subscriberCount?: number;
}

export interface InfluencerMarketing {
  enabled: boolean;
  platforms?: string[];
}

export interface MarketingInfo {
  brandAwareness?: number; // 1-10
  socialMedia?: {
    platform: string;
    url: string;
  }[];
  emailMarketing?: EmailMarketing;
  influencerMarketing?: InfluencerMarketing;
}

export interface CompanyProfile {
  companyInfo?: CompanyInfo;
  financialInfo?: FinancialInfo;
  productStockInfo?: ProductStockInfo;
  businessGoals?: BusinessGoals;
  operationalInfo?: OperationalInfo;
  marketingInfo?: MarketingInfo;
  updatedAt?: Date;
  completionPercentage?: number;
}

export type ProfileSection =
  | 'companyInfo'
  | 'financialInfo'
  | 'productStockInfo'
  | 'businessGoals'
  | 'operationalInfo'
  | 'marketingInfo';

// Turkish cargo companies
export const CARGO_COMPANIES = [
  'Yurtiçi Kargo',
  'Aras Kargo',
  'MNG Kargo',
  'Sürat Kargo',
  'PTT Kargo',
  'Sendeo',
  'Trendyol Express',
  'Hepsijet',
  'UPS',
  'DHL',
  'FedEx',
  'Horoz Lojistik',
  'Kolay Gelsin',
  'Scotty',
  'Getir',
];

// Social media platforms
export const SOCIAL_PLATFORMS = [
  { id: 'instagram', name: 'Instagram', icon: 'Instagram' },
  { id: 'facebook', name: 'Facebook', icon: 'Facebook' },
  { id: 'twitter', name: 'X (Twitter)', icon: 'Twitter' },
  { id: 'linkedin', name: 'LinkedIn', icon: 'Linkedin' },
  { id: 'tiktok', name: 'TikTok', icon: 'Music2' },
  { id: 'youtube', name: 'YouTube', icon: 'Youtube' },
  { id: 'pinterest', name: 'Pinterest', icon: 'Image' },
];

// Business sectors
export const BUSINESS_SECTORS = [
  'Moda & Giyim',
  'Elektronik',
  'Ev & Yaşam',
  'Kozmetik & Kişisel Bakım',
  'Gıda & İçecek',
  'Spor & Outdoor',
  'Oyuncak & Hobi',
  'Kitap & Kırtasiye',
  'Anne & Bebek',
  'Otomotiv',
  'Takı & Aksesuar',
  'Sağlık & Medikal',
  'Pet Shop',
  'Bahçe & Yapı Market',
  'Diğer',
];

// Turkish cities
export const TURKISH_CITIES = [
  'Adana', 'Adıyaman', 'Afyonkarahisar', 'Ağrı', 'Aksaray', 'Amasya', 'Ankara', 'Antalya',
  'Ardahan', 'Artvin', 'Aydın', 'Balıkesir', 'Bartın', 'Batman', 'Bayburt', 'Bilecik',
  'Bingöl', 'Bitlis', 'Bolu', 'Burdur', 'Bursa', 'Çanakkale', 'Çankırı', 'Çorum',
  'Denizli', 'Diyarbakır', 'Düzce', 'Edirne', 'Elazığ', 'Erzincan', 'Erzurum', 'Eskişehir',
  'Gaziantep', 'Giresun', 'Gümüşhane', 'Hakkari', 'Hatay', 'Iğdır', 'Isparta', 'İstanbul',
  'İzmir', 'Kahramanmaraş', 'Karabük', 'Karaman', 'Kars', 'Kastamonu', 'Kayseri', 'Kırıkkale',
  'Kırklareli', 'Kırşehir', 'Kilis', 'Kocaeli', 'Konya', 'Kütahya', 'Malatya', 'Manisa',
  'Mardin', 'Mersin', 'Muğla', 'Muş', 'Nevşehir', 'Niğde', 'Ordu', 'Osmaniye',
  'Rize', 'Sakarya', 'Samsun', 'Şanlıurfa', 'Siirt', 'Sinop', 'Sivas', 'Şırnak',
  'Tekirdağ', 'Tokat', 'Trabzon', 'Tunceli', 'Uşak', 'Van', 'Yalova', 'Yozgat', 'Zonguldak',
];
